import { PropsWithChildren, ReactNode } from 'react';

export type OtsFormFieldSize = 'compact' | 'default';

export type OtsFormFieldProps = PropsWithChildren<{
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  htmlFor: string;
  descriptionId?: string;
  errorId?: string;
  required?: boolean;
  size?: OtsFormFieldSize;
  /** Keeps the label for screen readers only, for a field whose purpose shows otherwise (a toolbar search). */
  hideLabel?: boolean;
  className?: string;
}>;

export const getOtsFormControlClassName = (
  size: OtsFormFieldSize = 'default',
  className?: string
) =>
  [
    'box-border w-full rounded-lg border border-[var(--ots-field-outline)] bg-[var(--ots-field)] text-[var(--ots-text)] shadow-sm outline-none transition placeholder:text-[var(--ots-text-muted)] focus:border-[var(--ots-primary)] focus:ring-2 focus:ring-[var(--ots-primary-container)] disabled:cursor-not-allowed disabled:bg-[var(--ots-surface-variant)] disabled:opacity-65',
    // `!` sizes: Mantine's unlayered reset sets inputs to the page's 16px, which beats Tailwind's classes in
    // DispatchGo and HubGo and left text inputs and dropdowns larger than the date fields beside them.
    size === 'compact'
      ? 'min-h-8 px-2.5 py-1 !text-xs'
      : 'min-h-10 px-3 py-2 !text-sm',
    className,
  ]
    .filter(Boolean)
    .join(' ');

/**
 * Shared label/help/error frame for native controls; compact keeps GWT-mounted forms economical.
 * Help text sits under the control, so label and control line up across a form row whether or
 * not a neighboring field has help text.
 */
export const OtsFormField = ({
  label,
  description,
  error,
  htmlFor,
  descriptionId,
  errorId,
  required,
  size = 'default',
  hideLabel = false,
  className,
  children,
}: OtsFormFieldProps) => {
  const compact = size === 'compact';

  return (
    <div
      className={[compact ? 'space-y-1' : 'space-y-1.5', className]
        .filter(Boolean)
        .join(' ')}
    >
      <label
        htmlFor={htmlFor}
        className={hideLabel ? 'sr-only' : [
          'block font-semibold text-[var(--ots-secondary)]',
          compact ? 'text-xs' : 'text-sm',
        ].join(' ')}
      >
        {label}
        {required && (
          <span aria-hidden="true" className="ml-1 text-[var(--ots-error)]">
            *
          </span>
        )}
      </label>
      {children}
      {description && (
        <p
          id={descriptionId}
          className={[
            'leading-5 text-[var(--ots-text-muted)]',
            compact ? 'text-[11px]' : 'text-xs',
          ].join(' ')}
        >
          {description}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          className={[
            'text-[var(--ots-error)]',
            compact ? 'text-[11px]' : 'text-xs',
          ].join(' ')}
        >
          {error}
        </p>
      )}
    </div>
  );
};
